import type { EmailComposeAttachment } from '@elva/ui';
import type { EmailAttachment, EmailMessageDetail, LegacyEmailThreadMessage } from '../api/types';
import { enrichWithCachedSentAttachments, rememberSentAttachments } from './sentAttachmentCache';

function readAttachmentName(row: EmailAttachment): string {
  return (row.filename ?? row.name ?? '').trim();
}

function readAttachmentId(row: EmailAttachment): string | undefined {
  const wire = row as EmailAttachment & Record<string, unknown>;
  for (const key of ['attachment_id', 'id', 'attachmentId', 'handle', 'file_id', 'uuid']) {
    const raw = wire[key];
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
    if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw);
  }
  return undefined;
}

function enrichMessageAttachments(
  attachments: EmailAttachment[],
  handles: string[],
  files: EmailComposeAttachment[],
): EmailAttachment[] {
  return attachments.map((row, index) => {
    if (readAttachmentId(row)) return row;

    const name = readAttachmentName(row);
    const fileMatch = files.find((entry) => entry.file.name === name);
    const file = fileMatch?.file ?? files[index]?.file;
    const handle = handles[index];
    if (!handle) return row;

    const contentType =
      (row.content_type ?? row.type ?? file?.type ?? '').trim() || undefined;
    const preview_url =
      row.preview_url ??
      (file?.type.startsWith('image/') ? URL.createObjectURL(file) : undefined);

    return {
      ...row,
      attachment_id: handle,
      ...(name ? { filename: name, name } : {}),
      ...(contentType ? { content_type: contentType, type: contentType } : {}),
      ...(preview_url ? { preview_url } : {}),
    };
  });
}

/** Bind upload handles (+ local image preview) onto fresh outbound attachment rows. */
export function enrichSentAttachments(
  detail: EmailMessageDetail,
  handles: string[] | undefined,
  files: EmailComposeAttachment[] | undefined,
): EmailMessageDetail {
  const threadId = detail.id?.trim();
  if (threadId && handles?.length && files?.length) {
    rememberSentAttachments(threadId, handles, files);
  }

  let enriched = detail;
  if (handles?.length && files?.length && detail.messages?.length) {
    const outboundIndexes = detail.messages
      .map((message, index) => ({ message, index }))
      .filter(({ message }) => message.direction === 'outbound' && message.attachments?.length);

    if (outboundIndexes.length) {
      const target = outboundIndexes[outboundIndexes.length - 1]!;
      const enrichedAttachments = enrichMessageAttachments(
        target.message.attachments ?? [],
        handles,
        files,
      );

      const messages = detail.messages.map((message, index) =>
        index === target.index ? { ...message, attachments: enrichedAttachments } : message,
      ) satisfies LegacyEmailThreadMessage[];

      enriched = { ...detail, messages };
    }
  }

  return enrichWithCachedSentAttachments(enriched);
}
